/**
 * The two bases this system genuinely uses, and they are not interchangeable.
 *
 * `BINARY` is what a file size is: Azure's block-blob limits are binary, and so
 * is every figure the upload path deals in. `DECIMAL` is what a **contract**
 * figure is — `shared/plans.ts` in the API says the same thing from the other
 * side, where `GB` is 10^9 because a contract's "25 GB" is decimal (PLN-2,
 * PLN-4).
 *
 * They are exported because the Storage surface renders both in one sentence
 * ("23.3 GB of 25 GB"), and picking the wrong one there is a silent error that
 * grows with the unit: 7% at GB, 10% at TB, which is where every band above B1
 * sits. A named constant at the call site is what makes the choice reviewable.
 */
export const BINARY = 1024;
export const DECIMAL = 1000;

/**
 * Bytes at the scale this system actually moves (UPL-3).
 *
 * One decimal place above bytes, because the difference between 8.0 GB and
 * 8.4 GB is the kind of thing somebody checks a transfer against — and none
 * below, because a tenth of a byte is not a thing.
 *
 * The units come in as an argument rather than living here: they are
 * user-facing strings, and the copy pattern puts those in a `*.copy.ts` next to
 * whatever renders them.
 *
 * `base` defaults to `BINARY`, which is what every caller before the Storage
 * surface wanted and what a file size is. Pass `DECIMAL` for anything measured
 * against a plan's allowance.
 */
export function formatBytes(
  bytes: number,
  units: readonly string[],
  base: number = BINARY,
): string {
  let value = bytes;
  let unit = 0;

  while (value >= base && unit < units.length - 1) {
    value /= base;
    unit += 1;
  }

  const label = units[unit] ?? units[0];
  return `${unit === 0 ? String(value) : value.toFixed(1)} ${String(label)}`;
}
